import AssessmentIcon from '@mui/icons-material/Assessment';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BugReportIcon from '@mui/icons-material/BugReport';
import FolderIcon from '@mui/icons-material/Folder';
import InsightsIcon from '@mui/icons-material/Insights';
import LockIcon from '@mui/icons-material/Lock';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import PersonIcon from '@mui/icons-material/Person';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import StorageIcon from '@mui/icons-material/Storage';
import TuneIcon from '@mui/icons-material/Tune';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import WarningIcon from '@mui/icons-material/Warning';
import { Alert, Box, Button, CircularProgress, Divider, Grid, LinearProgress, Paper, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import ServerManagementService from '../../services/ServerManagementService';
import { adminDashboardHelpItems } from '../../utils/helpItems';
import AdminPageWrapper from './AdminPageWrapper';

const AdminDashboard = ({ darkMode, toggleDarkMode, themeVariant }) => {
    const [serverStatus, setServerStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [shutdownInProgress, setShutdownInProgress] = useState(false);
    const [remainingSeconds, setRemainingSeconds] = useState(0);
    const countdownIntervalRef = useRef(null);

    // Cleanup interval on unmount
    useEffect(() => {
        return () => {
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
            }
        };
    }, []);

    // Check server status
    const checkServerStatus = async () => {
        try {
            setLoading(true);
            const authUser = JSON.parse(localStorage.getItem('authUser'));
            const authToken = JSON.parse(localStorage.getItem('authToken'));

            const status = await ServerManagementService.getServerStatus(authUser.email, authToken);
            setServerStatus(status);
            return status;
        } catch (error) {
            console.error('Error checking server status:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to check server status: ' + (error.response?.data?.message || error.message),
            });
            return null;
        } finally {
            setLoading(false);
        }
    };

    // Handle server shutdown
    const handleShutdown = async () => {
        // First check status
        const status = await checkServerStatus();
        if (!status) return;

        // Build warning message
        let warningMessage = 'This will shut down the server after 2 minutes.';
        let warnings = [];

        if (status.activeSessionCount > 0) {
            warnings.push(`<strong>${status.activeSessionCount}</strong> active editing session(s)`);
        }

        if (status.languageGenerationCount > 0) {
            warnings.push(`<strong>${status.languageGenerationCount}</strong> language(s) being generated/translated`);
        }

        if (warnings.length > 0) {
            warningMessage += '<br/><br/><strong>⚠️ Active Operations:</strong><br/>' + warnings.join('<br/>');
            warningMessage += '<br/><br/>These operations will be interrupted.';
        }

        warningMessage += '<br/><br/>Do you want to proceed?';

        const result = await Swal.fire({
            title: 'Shutdown Server?',
            html: warningMessage,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, schedule shutdown',
            cancelButtonText: 'Cancel',
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const authUser = JSON.parse(localStorage.getItem('authUser'));
                const authToken = JSON.parse(localStorage.getItem('authToken'));

                const response = await ServerManagementService.initiateShutdown(authUser.email, authToken);

                setShutdownInProgress(true);
                setRemainingSeconds(120);

                // Start countdown
                countdownIntervalRef.current = setInterval(() => {
                    setRemainingSeconds((prev) => {
                        if (prev <= 1) {
                            clearInterval(countdownIntervalRef.current);
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);

                Swal.fire({
                    icon: 'success',
                    title: 'Shutdown Scheduled',
                    html: `Server will shut down in <strong>2 minutes</strong>.<br/>You can cancel it before that.`,
                    timer: 3000,
                });
            } catch (error) {
                console.error('Error initiating shutdown:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Shutdown Failed',
                    text: 'Failed to initiate shutdown: ' + (error.response?.data?.message || error.message),
                });
            } finally {
                setLoading(false);
            }
        }
    };

    // Handle shutdown cancellation
    const handleCancelShutdown = async () => {
        const result = await Swal.fire({
            title: 'Cancel Shutdown?',
            text: 'Do you want to cancel the scheduled server shutdown?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, cancel shutdown',
            cancelButtonText: 'No',
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const authUser = JSON.parse(localStorage.getItem('authUser'));
                const authToken = JSON.parse(localStorage.getItem('authToken'));

                await ServerManagementService.cancelShutdown(authUser.email, authToken);

                setShutdownInProgress(false);
                setRemainingSeconds(0);

                if (countdownIntervalRef.current) {
                    clearInterval(countdownIntervalRef.current);
                }

                Swal.fire({
                    icon: 'success',
                    title: 'Shutdown Cancelled',
                    text: 'Server shutdown has been cancelled.',
                    timer: 2000,
                });
            } catch (error) {
                console.error('Error cancelling shutdown:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Cancellation Failed',
                    text: 'Failed to cancel shutdown: ' + (error.response?.data?.message || error.message),
                });
            } finally {
                setLoading(false);
            }
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <AdminPageWrapper
            title="Admin Dashboard"
            helpItems={adminDashboardHelpItems}
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
            themeVariant={themeVariant}
        >
            <Typography variant="h4" gutterBottom>
                Admin Dashboard
            </Typography>

            {/* Shutdown Warning Banner */}
            {shutdownInProgress && (
                <Alert
                    severity="error"
                    icon={<WarningIcon />}
                    sx={{ mb: 3 }}
                    action={
                        <Button color="inherit" size="small" onClick={handleCancelShutdown} disabled={loading}>
                            Cancel Shutdown
                        </Button>
                    }
                >
                    <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        ⚠️ SERVER SHUTDOWN IN PROGRESS
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Server will shut down in: <strong>{formatTime(remainingSeconds)}</strong>
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={((120 - remainingSeconds) / 120) * 100}
                        sx={{ mt: 1 }}
                        color="error"
                    />
                </Alert>
            )}

            {/* Team Management Section */}
            <Box sx={{ my: 4 }} className="team-management-section">
                <Typography
                    variant="h5"
                    sx={{
                        mb: 2,
                        pb: 1,
                        fontWeight: 500,
                        borderBottom: '2px solid',
                        borderColor: 'primary.main',
                        display: 'inline-block',
                    }}
                >
                    Team Management
                </Typography>

                <Grid container spacing={3}>
                    {/* User Management Card */}
                    <Grid item xs={12} md={6} lg={4} className="user-management-card">
                        <Paper
                            sx={{
                                p: 3,
                                textAlign: 'center',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                            }}
                            className="admin-dashboard-card"
                        >
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    User Management
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <PersonIcon sx={{ fontSize: 60, color: 'warning.main' }} />
                                </Box>
                                <Typography variant="body2" sx={{ mb: 3 }}>
                                    View and manage system users
                                </Typography>
                            </Box>

                            <Box sx={{ mt: 2 }}>
                                <Button
                                    component={Link}
                                    to="/admin/users"
                                    variant="contained"
                                    color="secondary"
                                    fullWidth
                                >
                                    Manage Users
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Permissions Groups Management Card */}
                    <Grid item xs={12} md={6} lg={4}>
                        <Paper
                            sx={{
                                p: 3,
                                textAlign: 'center',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                            }}
                            className="admin-dashboard-card"
                        >
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Group Management
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <PermContactCalendarIcon sx={{ fontSize: 60, color: 'primary.main' }} />
                                </Box>
                                <Typography variant="body2" sx={{ mb: 3 }}>
                                    Manage user groups and permission control
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    component={Link}
                                    to="/admin/groups"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                >
                                    Manage Groups
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>

                    {/*Folder Permission Management*/}
                    <Grid item xs={12} md={6} lg={4}>
                        <Paper
                            sx={{
                                p: 3,
                                textAlign: 'center',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                            }}
                            className="admin-dashboard-card"
                        >
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Folder Permissions
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <LockIcon sx={{ fontSize: 60, color: 'warning.main' }} />
                                </Box>
                                <Typography variant="body2" sx={{ mb: 3 }}>
                                    Manage folder access permissions for users and groups
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    component={Link}
                                    to="/admin/permissions"
                                    variant="contained"
                                    color="warning"
                                    fullWidth
                                >
                                    Manage Permissions
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Assignment Management Card - Currently disabled */}
                    {false && (
                        <Grid item xs={12} md={6} lg={4} className="assignment-management-card">
                            <Paper
                                sx={{
                                    p: 3,
                                    textAlign: 'center',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                }}
                                className="admin-dashboard-card"
                            >
                                <Box>
                                    <Typography variant="h6" gutterBottom>
                                        Assignment Management
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                        <AssignmentIcon sx={{ fontSize: 60, color: 'secondary.main' }} />
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" align="center">
                                        Manage video assignments and track progress. Assign videos to team members and set
                                        due dates.
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 2 }}>
                                    <Button
                                        component={Link}
                                        to="/admin/assignments"
                                        variant="contained"
                                        color="secondary"
                                        fullWidth
                                    >
                                        View Assignments
                                    </Button>
                                </Box>
                            </Paper>
                        </Grid>
                    )}

                    {/* Donor Impact Report Card */}
                    <Grid item xs={12} md={6} lg={4}>
                        <Paper
                            sx={{
                                p: 3,
                                textAlign: 'center',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                            }}
                            className="admin-dashboard-card"
                        >
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Donor Impact Report
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <AssessmentIcon sx={{ fontSize: 60, color: 'success.main' }} />
                                </Box>
                                <Typography variant="body2" sx={{ mb: 3 }}>
                                    Generate 30/90-day usage and server health reports for donors
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    component={Link}
                                    to="/admin/impact-report"
                                    variant="contained"
                                    color="success"
                                    fullWidth
                                >
                                    Generate Report
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Bug Reports Card */}
                    <Grid item xs={12} md={6} lg={4} className="bug-reports-card">
                        <Paper
                            sx={{
                                p: 3,
                                textAlign: 'center',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                            }}
                            className="admin-dashboard-card"
                        >
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Bug Reports
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <BugReportIcon sx={{ fontSize: 60, color: 'error.main' }} />
                                </Box>
                                <Typography variant="body2" sx={{ mb: 3 }}>
                                    View and manage user-submitted bug reports
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    component={Link}
                                    to="/admin/bug-reports"
                                    variant="contained"
                                    color="error"
                                    fullWidth
                                >
                                    View Bug Reports
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>

            {/* Admin Only Section */}
            <Box sx={{ my: 6, pt: 4 }} className="admin-tools-section">
                {/* Add a horizontal divider before the section */}
                <Divider
                    sx={{
                        my: 4,
                        borderColor: 'rgba(0, 0, 0, 0.12)',
                        borderWidth: 1,
                    }}
                />

                <Typography
                    variant="h5"
                    sx={{
                        mb: 2,
                        pb: 1,
                        fontWeight: 500,
                        borderBottom: '2px solid',
                        borderColor: 'primary.main',
                        display: 'inline-block',
                    }}
                >
                    Admin Only
                </Typography>

                <Grid container spacing={3}>
                    {/* Files & Folders Card */}
                    <Grid item xs={12} md={6} lg={4}>
                        <Paper
                            sx={{
                                p: 3,
                                textAlign: 'center',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                            }}
                            className="admin-dashboard-card"
                        >
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Files & Folders
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <FolderIcon sx={{ fontSize: 60, color: 'primary.main' }} />
                                </Box>
                                <Typography variant="body2" sx={{ mb: 3 }}>
                                    Browse and manage all files and folders in MongoDB
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Button component={Link} to="/admin/mongo-files" variant="contained" fullWidth>
                                    View Files & Folders
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Videos Card */}
                    <Grid item xs={12} md={6} lg={4}>
                        <Paper
                            sx={{
                                p: 3,
                                textAlign: 'center',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                            }}
                            className="admin-dashboard-card"
                        >
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Videos
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <VideoLibraryIcon sx={{ fontSize: 60, color: 'secondary.main' }} />
                                </Box>
                                <Typography variant="body2" sx={{ mb: 3 }}>
                                    Manage all video content and subtitles in MongoDB
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    component={Link}
                                    to="/admin/mongo-videos"
                                    variant="contained"
                                    color="secondary"
                                    fullWidth
                                >
                                    View Videos
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Storage Migration Card */}
                    <Grid item xs={12} md={6} lg={4}>
                        <Paper
                            sx={{
                                p: 3,
                                textAlign: 'center',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                            }}
                            className="admin-dashboard-card"
                        >
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Ops Tool
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <StorageIcon sx={{ fontSize: 60, color: 'info.main' }} />
                                </Box>
                                <Typography variant="body2" sx={{ mb: 3 }}>
                                    Run Various Scripts for Database and Application
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    component={Link}
                                    to="/admin/ops-tools"
                                    variant="contained"
                                    color="info"
                                    fullWidth
                                >
                                    Ops Tool
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Task tools Card */}
                    <Grid item xs={12} md={6} lg={4}>
                        <Paper
                            sx={{
                                p: 3,
                                textAlign: 'center',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                            }}
                            className="admin-dashboard-card"
                        >
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Task tools
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <TuneIcon sx={{ fontSize: 60, color: 'info.main' }} />
                                </Box>
                                <Typography variant="body2" sx={{ mb: 3 }}>
                                    English SRTs and form links, SmartSheet import, categories, platforms and deleted posts
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    component={Link}
                                    to="/tasks/tools"
                                    variant="contained"
                                    color="info"
                                    fullWidth
                                >
                                    Task tools
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Instrumentation Card */}
                    <Grid item xs={12} md={6} lg={4}>
                        <Paper
                            sx={{
                                p: 3,
                                textAlign: 'center',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                            }}
                            className="admin-dashboard-card"
                        >
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Instrumentation
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <InsightsIcon sx={{ fontSize: 60, color: 'primary.main' }} />
                                </Box>
                                <Typography variant="body2" sx={{ mb: 3 }}>
                                    View system metrics and dashboards
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    component={Link}
                                    to="/instrumentation"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                >
                                    View Instrumentation
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* Server Shutdown Card */}
                    <Grid item xs={12} md={6} lg={4}>
                        <Paper
                            sx={{
                                p: 3,
                                textAlign: 'center',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                border: '2px solid',
                                borderColor: shutdownInProgress ? 'error.main' : 'transparent',
                            }}
                            className="admin-dashboard-card"
                        >
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Server Control
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                    <PowerSettingsNewIcon
                                        sx={{ fontSize: 60, color: shutdownInProgress ? 'error.main' : 'error.light' }}
                                    />
                                </Box>
                                <Typography variant="body2" sx={{ mb: 3 }}>
                                    {shutdownInProgress
                                        ? '⚠️ Shutdown scheduled'
                                        : 'Safely shutdown the server with cleanup'}
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                {shutdownInProgress ? (
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        fullWidth
                                        onClick={handleCancelShutdown}
                                        disabled={loading}
                                        startIcon={loading && <CircularProgress size={20} />}
                                    >
                                        Cancel Shutdown
                                    </Button>
                                ) : (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        fullWidth
                                        onClick={handleShutdown}
                                        disabled={loading}
                                        startIcon={loading && <CircularProgress size={20} />}
                                    >
                                        Shutdown Server
                                    </Button>
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </AdminPageWrapper>
    );
};

export default AdminDashboard;
